/* eslint-disable import/order */
import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { I18nextProvider } from 'react-i18next'
import { toast } from 'react-toastify'
import { ApolloProvider } from '@apollo/client'
import { disableReactDevTools } from '@fvilers/disable-react-devtools'
import i18n from './i18n'
import { NotificationStylesProvider } from 'hooks/notifications/useNotification'
import * as serviceWorker from './serviceWorker'
import { store } from './store'
import { client } from 'client'
import * as userActions from 'store/actions/user.action'
import { ConnectedRouter } from 'connected-react-router'
import { history } from 'store/history'
import { Switch, Route, Redirect } from 'react-router'
import EditCourse from 'components/administration/components/courses/components/courses/edit/Edit'
import Hierarchy from 'components/configuration/components/hierarchy/Hierarchy'
import Users from 'components/configuration/components/users/Users'
import Get from 'components/courses/components/get/Get'
import NewCourse from 'components/administration/components/courses/components/courses/new/New'
import CoursesAdministration from 'components/administration/components/courses'
import TemplatesAdministration from 'components/administration/components/courses/templates'
import ExternalCoursesAdministration from 'components/administration/components/courses/externalCourses'
import LessonsIndex from 'components/administration/components/lessons/Index'
import BlockIndex from 'components/administration/components/blocks/Index'
import LessonView from 'components/courses/lessons/View'
import AdminCoursesWrapper from 'components/administration/view/View'
import Loader from 'components/ui/loader/Loader'
import CoursePreview from 'components/administration/components/lessons/components/preview/Index'
import Evaluation from 'components/courses/lessons/components/LessonCard/components/ContentBlock/components/evaluation/Evaluation'
import NewHome from './components/new-home/NewHome'
import 'react-toastify/dist/ReactToastify.css'
import 'antd/dist/antd.css'
import 'scss/bootstrap.scss'
import 'scss/lms.scss'
import '@rankmi/rankmi-table/dist/index.css'
import NotFound from 'components/errorPages/notFound'
import Forbidden from 'components/errorPages/forbidden'
import Generate from './components/home/components/my-certificates/components/Generate'
import { ADMIN_ROUTE, PathRoutes, ProfileRoutes } from 'enum/routes'
import { paths } from 'routes/paths'
import { Monitoring } from 'components/monitoring'
import ScormPage from 'components/scorm'
import { RankmiAmplitudeWrapper } from 'contexts'
import { EvaluationStatesEnum } from 'enum/evaluation-states.enum'
import { MainDataProvider } from 'components/monitoring/context'
import { CHAMELEON_KEY, isProd } from 'config'
import chmln from '@chamaeleonidae/chmln'
import { useChameleon } from 'hooks/useChameleon'
import { canActiveChmln } from 'helpers/chameleon.helper'
import { LmsProfilesPage } from 'pages/configuration/LmsProfiles'
import { AccessConsumer, AccessProvider } from 'contexts/AccessContext'
import RequireAccess from 'components/reusables/requireAccess'
import RequireInternalRequestsAccess from 'components/reusables/requireAccess/RequireInternalRequestsAccess'
import IntegrationsConfig from 'components/configuration/components/integrations/IntegrationsConfig'
import MyCourses from 'components/home/components/my-courses'
import MyCertificates from 'components/home/components/my-certificates/MyCertificates'
import ExternalCourses from 'components/home/components/externalCourses'
import AdminApplications from 'components/home/components/admin-applications/AdminApplications'
import { Messages } from 'components/home/components/messages/Messages'
import { ViewAll } from 'components/home/components/view-all/ViewAll'
import Home from 'pages/Home'
import { Library } from 'pages/Library'
import { LearningPath } from 'pages/LearningPath'
import LearningPathsManage from 'components/administration/components/learningPaths/manage'
import UserProfile from 'components/configuration/components/userProfile'
import ParticipantCourseResults from 'components/configuration/components/userProfile/ParticipantCourseResults'
import RolesAndPermissionsRoutes from 'components/configuration/components/rolesAndPermissions/Routes'
import ConfigurationIndexRedirect from 'components/configuration/components/ConfigurationIndexRedirect'
import { Webinars } from 'components/webinars'
import { ReaderMessageProvider } from 'contexts/ReaderMessageContext'
import ExternalCourseAdministration from 'components/administration/components/courses/externalCourse'

// Inyectar favicons y apple-touch-icon remotos basados en variable de entorno
const RANKMI_ASSETS_BASE = process.env.REACT_APP_RANKMI_LOGO_URL
function setOrReplaceLink(rel: string, href: string, type?: string) {
  if (!href) return
  const existing =
    document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`) ||
    document.createElement('link')
  existing.rel = rel
  existing.href = href
  if (type) existing.type = type
  if (!existing.parentNode) {
    document.head.appendChild(existing)
  }
}
if (RANKMI_ASSETS_BASE) {
  // Ambos enlaces apuntan al .ico remoto, según requerimiento
  setOrReplaceLink('icon', `${RANKMI_ASSETS_BASE}/favicon.ico`, 'image/x-icon')
  setOrReplaceLink(
    'shortcut icon',
    `${RANKMI_ASSETS_BASE}/favicon.ico`,
    'image/x-icon'
  )
}

const Requests = React.lazy(
  () => import('./components/home/components/request-integration/Request')
)

if (canActiveChmln()) {
  try {
    chmln?.init(CHAMELEON_KEY, {
      fastUrl: 'https://fast.chameleon.io/',
    })
  } catch (error) {
    console.error('Chameleon init failed', error)
  }
}

const ImportCenter = React.lazy(
  () => import('components/administration/components/ImportCenter')
)

const AuditRoutes = React.lazy(() => import('pages/configuration/Audit'))

const LearningPaths = React.lazy(
  () => import('components/administration/components/learningPaths')
)

const CertificatesGenerate = React.lazy(
  () => import('components/home/components/CertificatesGenerate')
)

const DownloadCertificate = React.lazy(
  () => import('components/home/components/DownloadCertificate')
)
const Dc3CertificateDownload = React.lazy(
  () => import('components/home/components/Dc3CertificateDownload')
)

const TranslationsConfig = React.lazy(
  () => import('components/configuration/components/translations/Translations')
)

const Reports = React.lazy(() => import('components/reports'))

toast.configure({
  className: 'toast-container',
  autoClose: 5000,
})

i18n.on('initialized', () => {
  console.log('i18n initialized')
})

store.dispatch({ type: userActions.DECODE_JWT })
// store.dispatch({ type: lmsProfileActions.LMS_PROFILES_LIST_GET, payload: { page: 1, searchText: "", sort: [] } });

const Contexts: React.FC = ({ children }) => (
  <ApolloProvider client={client}>
    <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
  </ApolloProvider>
)

/** Debe montarse dentro de AccessProvider (useAccess). */
const ChameleonBootstrap: React.FC = () => {
  useChameleon()
  return null
}

const AppRoutes: React.FC = ({ children }) => {
  return (
    <AccessProvider>
      <ChameleonBootstrap />
      <ReaderMessageProvider>
        <Switch>
          <Route exact path={PathRoutes.SCORM} component={ScormPage} />{' '}
          <Route
            exact
            path={PathRoutes.COURSE_CERTIFICATES_GENERATE}
            component={CertificatesGenerate}
          />
          <Route
            exact
            path={PathRoutes.DOWNLOAD_CERTIFICATE}
            component={DownloadCertificate}
          />
          <Route
            exact
            path={PathRoutes.DC3_CERTIFICATE_DOWNLOAD}
            component={Dc3CertificateDownload}
          />
          <Route exact path={paths.forbidden} component={Forbidden} />
          <Route exact path="/notFound" component={NotFound} />
          <Route path="*">{children}</Route>
        </Switch>
      </ReaderMessageProvider>
    </AccessProvider>
  )
}

const Layout = ({ children }) => {
  return <NewHome>{children}</NewHome>
}

if (isProd) disableReactDevTools()

ReactDOM.render(
  <Contexts>
    <RankmiAmplitudeWrapper>
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <Suspense fallback={<Loader overlay color />}>
            <NotificationStylesProvider />
            <AppRoutes>
              <Layout>
                <Switch>
                  <Route path={PathRoutes.REPORTS}>
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <Reports />
                    </RequireAccess>
                  </Route>
                  <Route exact path="/" component={Home} />
                  <Route exact path="/library" component={Library} />
                  <Route
                    exact
                    path="/certificates"
                    component={MyCertificates}
                  />
                  <Route
                    exact
                    path="/my-courses/:origin?"
                    component={MyCourses}
                  />

                  <Route exact path="/messages" component={Messages} />

                  <Route exact path="/admin-applications">
                    <RequireAccess
                      allow={({ can }) => can('manageCourseRequests')}
                    >
                      <AdminApplications />
                    </RequireAccess>
                  </Route>
                  <Route
                    exact
                    path="/external-courses"
                    component={ExternalCourses}
                  />
                  <Route exact path="/requests">
                    <RequireInternalRequestsAccess>
                      <Requests />
                    </RequireInternalRequestsAccess>
                  </Route>
                  <Route exact path="/courses/:courseId/" component={Get} />
                  <Route
                    exact
                    path={PathRoutes.CERTIFICATE_GENERATE}
                    render={({ match }) => {
                      const { certificateId, courseId } = match.params
                      return (
                        <div
                          style={{
                            overflow: 'hidden',
                            height: '0px',
                          }}
                        >
                          <Generate
                            certificateId={certificateId}
                            courseId={courseId}
                          />
                        </div>
                      )
                    }}
                  />
                  {/* Si se necesita envolver un componente en otro, se debe usar un wrapper */}
                  {/* TODO: Buscar una mejor forma de hacerlo. Menos lineas  */}
                  <Route
                    exact
                    path="/courses/:courseId/lessons"
                    component={LessonView}
                  />

                  <Route path={ADMIN_ROUTE} exact>
                    <RequireAccess
                      allow={({ modules, can }) =>
                        modules.administration ||
                        modules.monitoring ||
                        can('manageLearningPaths')
                      }
                    >
                      <AccessConsumer>
                        {({ modules, can, access }) => {
                          if (access.canAccessInternalCoursesList()) {
                            return <Redirect to={PathRoutes.COURSES} />
                          }
                          if (can('manageExternalCourses')) {
                            return <Redirect to={PathRoutes.EXTERNAL_COURSES} />
                          }
                          if (modules.monitoring) {
                            return <Redirect to={PathRoutes.MONITORING} />
                          }
                          return <Redirect to={paths.forbidden} />
                        }}
                      </AccessConsumer>
                    </RequireAccess>
                  </Route>

                  {/* Admin COURSES*/}
                  <Route path={PathRoutes.COURSES}>
                    <RequireAccess
                      allow={({ modules, can, access }) =>
                        modules.administration ||
                        access.canAccessCourseManagementView() ||
                        can('viewCourseDetails')
                      }
                    >
                      <AdminCoursesWrapper>
                        <Switch>
                          <Route exact path="/administration/courses/new">
                            <RequireAccess
                              allow={({ can }) => can('manageInternalCourses')}
                            >
                              <NewCourse />
                            </RequireAccess>
                          </Route>
                          <Route path="/administration/courses/:courseId/edit">
                            <RequireAccess
                              allow={({ can, access }) =>
                                can('manageExternalCourses') ||
                                access.canEditCourseFromList()
                              }
                            >
                              <EditCourse />
                            </RequireAccess>
                          </Route>
                          {/* Admin LESSONS */}
                          <Route
                            exact
                            path="/administration/courses/:courseId/lessons"
                          >
                            <LessonsIndex />
                          </Route>
                          <Route path="/administration/courses/:courseId/lessons/:lessonId/edit">
                            <RequireAccess
                              allow={({ can }) => can('manageInternalCourses')}
                            >
                              <BlockIndex />
                            </RequireAccess>
                          </Route>
                          <Route exact path={PathRoutes.COURSE_PREVIEW}>
                            <RequireAccess
                              allow={({ access }) => access.canPreviewCourse()}
                            >
                              <CoursePreview />
                            </RequireAccess>
                          </Route>
                          <Route exact path={PathRoutes.TEMPLATES}>
                            <Redirect
                              to={PathRoutes.CERTIFICATE_TEMPLATES_STANDARD}
                            />
                          </Route>
                          <Route path={`${PathRoutes.TEMPLATES}/:tab`}>
                            <RequireAccess
                              allow={({ can }) => can('manageInternalCourses')}
                            >
                              <TemplatesAdministration />
                            </RequireAccess>
                          </Route>
                          <Route path={PathRoutes.EXTERNAL_COURSE}>
                            <RequireAccess
                              allow={({ can }) => can('manageExternalCourses')}
                            >
                              <ExternalCourseAdministration />
                            </RequireAccess>
                          </Route>
                          <Route path={PathRoutes.EXTERNAL_COURSES}>
                            <RequireAccess
                              allow={({ can }) => can('manageExternalCourses')}
                            >
                              <ExternalCoursesAdministration />
                            </RequireAccess>
                          </Route>
                          <Route exact path={PathRoutes.COURSES}>
                            <AccessConsumer>
                              {({ access, can, loading }) => {
                                if (loading) return <Loader overlay color />
                                if (access.canAccessInternalCoursesList()) {
                                  return <CoursesAdministration />
                                }
                                if (can('manageExternalCourses')) {
                                  return (
                                    <Redirect
                                      to={PathRoutes.EXTERNAL_COURSES}
                                    />
                                  )
                                }
                                return <Redirect to={paths.forbidden} />
                              }}
                            </AccessConsumer>
                          </Route>
                        </Switch>
                      </AdminCoursesWrapper>
                    </RequireAccess>
                  </Route>
                  <Route path={PathRoutes.MONITORING}>
                    <AdminCoursesWrapper>
                      <MainDataProvider>
                        <Switch>
                          <Route path={PathRoutes.MONITORING_EXTERNAL_COURSE}>
                            <RequireAccess
                              allow={({ access }) =>
                                access.canAccessCourseSheet()
                              }
                            >
                              <Monitoring />
                            </RequireAccess>
                          </Route>
                          <Route exact path={PathRoutes.MONITORING_COURSE}>
                            <RequireAccess
                              allow={({ access }) =>
                                access.canAccessCourseSheet()
                              }
                            >
                              <Monitoring />
                            </RequireAccess>
                          </Route>
                          <Route path={PathRoutes.MONITORING}>
                            <RequireAccess
                              allow={({ can }) => can('viewMonitoring')}
                            >
                              <Monitoring />
                            </RequireAccess>
                          </Route>
                        </Switch>
                      </MainDataProvider>
                    </AdminCoursesWrapper>
                  </Route>
                  <Route exact path={PathRoutes.IMPORT_CENTER}>
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <AdminCoursesWrapper>
                        <ImportCenter />
                      </AdminCoursesWrapper>
                    </RequireAccess>
                  </Route>
                  {/* Evaluation */}
                  <Route
                    exact
                    path="/evaluation/:evaluationId/check"
                    render={({ match }) => (
                      <Evaluation
                        state={EvaluationStatesEnum.onCheck}
                        evaluationId={match.params.evaluationId}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/administration/evaluation/:evaluationId/preview"
                    render={({ match }) => (
                      <RequireAccess
                        allow={({ can }) => can('manageInternalCourses')}
                      >
                        <Evaluation
                          state={EvaluationStatesEnum.onPreview}
                          evaluationId={match.params.evaluationId}
                        />
                      </RequireAccess>
                    )}
                  />
                  <Route path={PathRoutes.LEARNING_PATHS}>
                    <RequireAccess
                      allow={({ modules }) => modules.learningPaths}
                    >
                      <Switch>
                        <Route exact path={PathRoutes.LEARNING_PATHS}>
                          <LearningPaths />
                        </Route>
                        <Route path={PathRoutes.LEARNING_PATHS_CREATE}>
                          <RequireAccess
                            allow={({ can }) => can('manageLearningPaths')}
                          >
                            <LearningPathsManage />
                          </RequireAccess>
                        </Route>
                        <Route path={PathRoutes.LEARNING_PATHS_UPDATE}>
                          <RequireAccess
                            allow={({ can }) => can('manageLearningPaths')}
                          >
                            <LearningPathsManage />
                          </RequireAccess>
                        </Route>
                      </Switch>
                    </RequireAccess>
                  </Route>
                  <Route exact path="/configuration">
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <ConfigurationIndexRedirect />
                    </RequireAccess>
                  </Route>
                  <Route exact path="/configuration/">
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <ConfigurationIndexRedirect />
                    </RequireAccess>
                  </Route>
                  <Route
                    exact
                    path="/configuration/user-profile/:userId/courses/:courseId/results"
                  >
                    <RequireAccess
                      allow={({ access }) => access.canViewParticipantResults()}
                    >
                      <ParticipantCourseResults />
                    </RequireAccess>
                  </Route>
                  <Route
                    exact
                    path="/configuration/user-profile/:userId"
                    render={(props) => (
                      <RequireAccess
                        allow={({ access }) =>
                          access.canViewParticipantProfile()
                        }
                      >
                        <UserProfile
                          userId={Number(props.match.params.userId)}
                        />
                      </RequireAccess>
                    )}
                  />
                  <Route exact path="/configuration/hierarchy/">
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <Hierarchy />
                    </RequireAccess>
                  </Route>
                  <Route path={ProfileRoutes.MAIN}>
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <LmsProfilesPage />
                    </RequireAccess>
                  </Route>
                  <Route exact path="/configuration/users/">
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <Users />
                    </RequireAccess>
                  </Route>
                  <Route exact path="/configuration/translations">
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <TranslationsConfig />
                    </RequireAccess>
                  </Route>
                  <Route exact path="/configuration/integrations">
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <IntegrationsConfig />
                    </RequireAccess>
                  </Route>
                  <Route path={PathRoutes.ROLES_AND_PERMISSIONS}>
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <RolesAndPermissionsRoutes />
                    </RequireAccess>
                  </Route>
                  <Route path={PathRoutes.AUDIT}>
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <AuditRoutes />
                    </RequireAccess>
                  </Route>
                  <Route
                    path="/view-all/:scope/:filter_by/:folder_id?/:folder_title?/:origin?"
                    component={ViewAll}
                  />
                  <Route path="/learning-path/:id" component={LearningPath} />
                  <Route exact path="/webinars">
                    <RequireAccess allow={({ isAdmin }) => isAdmin()}>
                      <Webinars />
                    </RequireAccess>
                  </Route>
                  <Redirect to="/notFound" />
                </Switch>
              </Layout>
            </AppRoutes>
          </Suspense>
        </ConnectedRouter>
      </Provider>
    </RankmiAmplitudeWrapper>
  </Contexts>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
